import { Box, Link, Stack, Typography } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useMemo } from 'react';

import { StyledDivider } from '@/components/common/StyledDivider';
import { PickupParkingFacilityDetailContent } from '@/components/map/details/content/PickupParkingFacilityDetailContent';
import { PickupParkingFacilityDetailTable } from '@/components/map/details/table/PickupParkingFacilityDetailTable';
import { pickupParkingFacilityFeatureCollection } from '@/data/pickupParkingFacilityFeatureCollection';
import { PICKUP_PARKING_FACILITY_PHOTO_FIELDS } from '@/types/PickupParkingFacilityFeature';

export const Route = createFileRoute('/_public/_map/_sidebar/pickup-parking-facilities/$pickupParkingFacilityId')({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
});

function RouteComponent() {
  const { pickupParkingFacilityId } = Route.useParams();

  const pickupParkingFacilityFeature = useMemo(
    () =>
      pickupParkingFacilityFeatureCollection.features.find(
        (feature) => feature.properties.id === pickupParkingFacilityId,
      ),
    [pickupParkingFacilityId],
  );

  const photos = pickupParkingFacilityFeature
    ? PICKUP_PARKING_FACILITY_PHOTO_FIELDS.map(({ url, text, citation, usageConstraints }) => {
        const urlValue = pickupParkingFacilityFeature.properties[url];
        if (!urlValue) return null;

        return {
          url: urlValue,
          text: pickupParkingFacilityFeature.properties[text],
          citation: pickupParkingFacilityFeature.properties[citation],
          usageConstraints: pickupParkingFacilityFeature.properties[usageConstraints],
        };
      }).filter(
        (
          p,
        ): p is {
          url: string;
          text: string | null;
          citation: string | null;
          usageConstraints: string | null;
        } => p !== null,
      )
    : [];

  if (!pickupParkingFacilityFeature) {
    return null;
  }

  return (
    <Box sx={{ overflow: 'auto' }}>
      <Stack py={4}>
        {pickupParkingFacilityFeature ? (
          <PickupParkingFacilityDetailContent facility={pickupParkingFacilityFeature} />
        ) : null}
      </Stack>

      <StyledDivider />

      {pickupParkingFacilityFeature?.properties.infotextlong ? (
        <Fragment>
          <Typography marginBlock={4}>{pickupParkingFacilityFeature.properties.infotextlong}</Typography>
          <StyledDivider />
        </Fragment>
      ) : null}

      <Stack gap={1.5} py={4}>
        {pickupParkingFacilityFeature ? <PickupParkingFacilityDetailTable data={pickupParkingFacilityFeature} /> : null}

        {pickupParkingFacilityFeature?.properties.timetablelink ? (
          <Stack gap={0.5} px={2}>
            <Typography lang="de" variant="caption">
              ÖPNV-Haltestelle in der Nähe
            </Typography>

            {pickupParkingFacilityFeature?.properties.timetablelink ? (
              <Link
                href={pickupParkingFacilityFeature.properties.timetablelink.source}
                rel="noreferrer"
                sx={{ textDecoration: 'none' }}
                target="_blank"
              >
                {(pickupParkingFacilityFeature.properties.timetablelink.text ||
                  pickupParkingFacilityFeature?.properties.frequencybustrainconnection) ??
                  'Zum Abfahrtsplan der Haltestelle'}
              </Link>
            ) : null}
          </Stack>
        ) : null}

        {photos.length > 0 && (
          <Stack gap={1} mb={2} px={2}>
            <Typography lang="de" variant="caption">
              Fotos
            </Typography>

            {photos.map((photo) => (
              <Box key={photo.url}>
                <Box
                  alt={photo.text ?? 'P+M-Foto'}
                  component="img"
                  src={photo.url}
                  sx={{
                    width: '100%',
                    height: 180,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
                {photo.text || photo.citation ? (
                  <Typography color="text.secondary" display="block" mt={0.5} variant="caption">
                    {photo.text}
                    {photo.citation ? ` (${photo.citation})` : ''}
                  </Typography>
                ) : null}
              </Box>
            ))}
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
